<template>
  <div class="announcement-management">
    <div class="page-header">
      <h2>公告管理</h2>
      <el-button type="primary" @click="showCreateDialog">
        <el-icon><Plus /></el-icon>
        新建公告
      </el-button>
    </div>
    
    <div class="filter-bar">
      <el-select v-model="filterStatus" placeholder="状态筛选" clearable @change="loadAnnouncements" style="width: 150px;">
        <el-option label="全部" value="" />
        <el-option label="草稿" value="draft" />
        <el-option label="已发布" value="published" />
      </el-select>
    </div>
    
    <el-table :data="announcements" style="width: 100%" v-loading="loading">
      <el-table-column prop="title" label="标题" min-width="200" show-overflow-tooltip />
      <el-table-column prop="status" label="状态" width="100">
        <template #default="scope">
          <el-tag :type="scope.row.status === 'published' ? 'success' : 'info'">
            {{ scope.row.status === 'published' ? '已发布' : '草稿' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="priority" label="优先级" width="100" />
      <el-table-column prop="is_pinned" label="置顶" width="80">
        <template #default="scope">
          <el-icon :color="scope.row.is_pinned ? '#F56C6C' : '#C0C4CC'">
            <StarFilled v-if="scope.row.is_pinned" />
            <Star v-else />
          </el-icon>
        </template>
      </el-table-column>
      <el-table-column prop="publish_at" label="发布时间" width="180">
        <template #default="scope">
          {{ scope.row.publish_at || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="expires_at" label="过期时间" width="180">
        <template #default="scope">
          {{ scope.row.expires_at || '永不过期' }}
        </template>
      </el-table-column>
      <el-table-column prop="created_by_name" label="创建者" width="100" show-overflow-tooltip />
      <el-table-column label="操作" width="150" fixed="right">
        <template #default="scope">
          <el-button size="small" type="primary" @click="editAnnouncement(scope.row)">
            编辑
          </el-button>
          <el-button size="small" type="danger" @click="deleteAnnouncement(scope.row)">
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页 -->
    <el-pagination
      style="margin-top: 20px; text-align: right;"
      background
      layout="prev, pager, next, jumper, ->, total"
      :total="total"
      :page-size="pageSize"
      v-model:current-page="currentPage"
      @current-change="handlePageChange"
    />
    
    <!-- 创建/编辑弹窗 -->
    <el-dialog
      :title="editMode ? '编辑公告' : '新建公告'"
      v-model="dialogVisible"
      width="800px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入公告标题" maxlength="255" />
        </el-form-item>
        
        <el-form-item label="内容" prop="content">
          <el-input
            v-model="form.content"
            type="textarea"
            :rows="10"
            placeholder="请输入公告内容，支持 Markdown 格式"
          />
          <div class="markdown-tip">
            <el-icon><InfoFilled /></el-icon>
            支持 Markdown 格式：标题、列表、链接、代码块等
          </div>
        </el-form-item>
        
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="状态" prop="status">
              <el-select v-model="form.status" style="width: 100%">
                <el-option label="草稿" value="draft" />
                <el-option label="已发布" value="published" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="优先级" prop="priority">
              <el-input-number v-model="form.priority" :min="0" :max="999" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="置顶">
              <el-switch v-model="form.is_pinned" @change="handlePinnedChange" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="发布时间" prop="publish_at">
              <el-date-picker
                v-model="form.publish_at"
                type="datetime"
                placeholder="选择发布时间"
                style="width: 100%"
                format="YYYY-MM-DD HH:mm:ss"
                value-format="YYYY-MM-DD HH:mm:ss"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="过期时间">
              <el-date-picker
                v-model="form.expires_at"
                type="datetime"
                placeholder="选择过期时间（可选）"
                style="width: 100%"
                format="YYYY-MM-DD HH:mm:ss"
                value-format="YYYY-MM-DD HH:mm:ss"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="saving">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, StarFilled, Star, InfoFilled } from '@element-plus/icons-vue';
import { 
  getAnnouncementsAdmin, 
  createAnnouncement, 
  updateAnnouncement, 
  deleteAnnouncement as deleteAnnouncementAPI
} from '../api/announcement';

export default {
  name: 'AnnouncementManagement',
  components: {
    Plus,
    StarFilled,
    Star,
    InfoFilled
  },
  setup() {
    const announcements = ref([]);
    const loading = ref(false);
    const saving = ref(false);
    const filterStatus = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const dialogVisible = ref(false);
    const editMode = ref(false);
    const formRef = ref();
    
    const form = reactive({
      id: null,
      title: '',
      content: '',
      status: 'draft',
      priority: 0,
      is_pinned: false,
      publish_at: '',
      expires_at: ''
    });
    
    const rules = {
      title: [
        { required: true, message: '请输入公告标题', trigger: 'blur' }
      ],
      content: [
        { required: true, message: '请输入公告内容', trigger: 'blur' }
      ],
      status: [
        { required: true, message: '请选择状态', trigger: 'change' }
      ]
    };
    
    const loadAnnouncements = async () => {
      try {
        loading.value = true;
        const params = {
          page: currentPage.value,
          page_size: pageSize.value
        };
        if (filterStatus.value) {
          params.status = filterStatus.value;
        }
        
        const response = await getAnnouncementsAdmin(params);
        if (response.success) {
          announcements.value = response.data || [];
          total.value = response.total || 0;
        } else {
          ElMessage.error(response.message || '加载公告列表失败');
        }
      } catch (error) {
        console.error('加载公告列表失败:', error);
        ElMessage.error('加载公告列表失败');
      } finally {
        loading.value = false;
      }
    };
    
    const handlePageChange = (page) => {
      currentPage.value = page;
      loadAnnouncements();
    };
    
    const showCreateDialog = () => {
      editMode.value = false;
      resetForm();
      dialogVisible.value = true;
    };
    
    const editAnnouncement = (announcement) => {
      editMode.value = true;
      Object.assign(form, {
        id: announcement.id,
        title: announcement.title,
        content: announcement.content,
        status: announcement.status,
        priority: announcement.priority,
        is_pinned: announcement.is_pinned,
        publish_at: announcement.publish_at || '',
        expires_at: announcement.expires_at || ''
      });
      dialogVisible.value = true;
    };
    
    const deleteAnnouncement = async (announcement) => {
      try {
        await ElMessageBox.confirm(
          `确定要删除公告 "${announcement.title}" 吗？`,
          '确认删除',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        );
        
        const response = await deleteAnnouncementAPI(announcement.id);
        if (response.success) {
          ElMessage.success('删除成功');
          loadAnnouncements();
        } else {
          ElMessage.error(response.message || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error);
          ElMessage.error('删除失败');
        }
      }
    };
    
    const handlePinnedChange = (value) => {
      if (value) {
        form.priority = 999;
      }
    };
    
    const resetForm = () => {
      form.id = null;
      form.title = '';
      form.content = '';
      form.status = 'draft';
      form.priority = 0;
      form.is_pinned = false;
      form.publish_at = '';
      form.expires_at = '';
      if (formRef.value) {
        formRef.value.resetFields();
      }
    };
    
    const handleSubmit = async () => {
      try {
        await formRef.value.validate();
        saving.value = true;
        
        const data = {
          title: form.title.trim(),
          content: form.content.trim(),
          status: form.status,
          priority: form.priority,
          is_pinned: form.is_pinned,
          publish_at: form.publish_at || null,
          expires_at: form.expires_at || null
        };
        
        let response;
        if (editMode.value) {
          response = await updateAnnouncement(form.id, data);
        } else {
          response = await createAnnouncement(data);
        }
        
        if (response.success) {
          ElMessage.success(editMode.value ? '更新成功' : '创建成功');
          dialogVisible.value = false;
          loadAnnouncements();
        } else {
          ElMessage.error(response.message || (editMode.value ? '更新失败' : '创建失败'));
        }
      } catch (error) {
        console.error('保存失败:', error);
        if (error !== false) { // validate 失败会返回 false
          ElMessage.error('保存失败');
        }
      } finally {
        saving.value = false;
      }
    };
    
    onMounted(() => {
      loadAnnouncements();
    });
    
    return {
      announcements,
      loading,
      saving,
      filterStatus,
      currentPage,
      pageSize,
      total,
      dialogVisible,
      editMode,
      form,
      rules,
      formRef,
      loadAnnouncements,
      handlePageChange,
      showCreateDialog,
      editAnnouncement,
      deleteAnnouncement,
      handlePinnedChange,
      resetForm,
      handleSubmit
    };
  }
};
</script>

<style scoped>
.announcement-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin: 0;
}

.filter-bar {
  margin-bottom: 20px;
}

.markdown-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>

